import { Component, EventEmitter, HostListener, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ColDef, ColGroupDef, RowGroupingDisplayType } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { AgNoRowsOverlayComponent } from '../../../../ag-grid-components/ag-no-rows-overlay/ag-no-rows-overlay.component';
import { DxsrExcelStyling } from '../dxsr-excel-styling';
import { AgGridFunctions } from '../../../../../helpers/ag-grid-functions';
import { ProgressAllocationService } from '../../../../../services/progress/progress-allocation.service';
import { CreateRangeChartParams, FirstDataRenderedEvent } from 'ag-grid-community';
import { ProgressAllocationSnapshotRequest } from '../../../../../models/progress-allocation-snapshot-request';
import { AgLoadingOverlayComponent } from '../../../../ag-grid-components/ag-loading-overlay/ag-loading-overlay.component';
import { NgStyle } from '@angular/common';
import { AgGridExportHelper, IExportableColDef, IQueryFilterRow } from '../../../../../helpers/ag-grid-export-helper';
import { PayerItem } from '../../../../../models/payer-item';
import { ReadabilityHelper } from '../../../../../helpers/readability-helper';
import { ClientToastController } from '../../../../../toasts/client-toast.lib';
import { UserOption } from '../../../../field-components/user-search-by-permission/user-search-by-permission.component';

export interface ProgressAllocationSnapshotReportOptions {
  mode?: "user" | "preferred-action" | "payer";
}

@Component({
    selector: 'app-progress-allocation-snapshot-report',
    templateUrl: './progress-allocation-snapshot-report.component.html',
    styleUrls: ['./progress-allocation-snapshot-report.component.scss'],
    imports: [NgStyle, AgGridModule]
})
export class ProgressAllocationSnapshotReportComponent implements OnInit, OnDestroy {

  private readonly intervalLabelMap: { [key: number]: string } = {
    1: 'Daily',
    7: 'Weekly',
    30: 'Monthly'
  };

  @Input() options = {mode: "user"};
  @Input() payerOptions: PayerItem[] = [];
  @Input() userOptions: UserOption[] = [];

  data: any[] = [];
  currentRequest: ProgressAllocationSnapshotRequest = new ProgressAllocationSnapshotRequest();

  exportTitle = '';

  columnDefs: (ColDef | ColGroupDef)[] = [];

  defaultColDef: ColDef = {
    menuTabs: [],
    minWidth: 100,
    maxWidth: 100,
    autoHeight: true
  };

  groupDisplayType: RowGroupingDisplayType = 'groupRows';

  autoGroupColumnDef: ColDef = {
    minWidth: 200,
    cellClass: 'indent-1'
  };

  noRowsOverlayComponent: any = AgNoRowsOverlayComponent;
  noRowsOverlayComponentParams: any = {
    noRowsMessageFunc: () => 'No results returned',
  };

  loadingOverlayComponent: any = AgLoadingOverlayComponent;

  excelStyles = DxsrExcelStyling.excelStyles2;

  gridContext: any;

  rowItems: any[] = [];
  gridReady = false;

  dxSwitch = false;

  currentStartYear = 0;
  currentEndYear = 0;

  currentRowHeight = 140;
  currentGridWidth = 282;

  subs: Subscription[] = [];

  agGridExportHelper: AgGridExportHelper = new AgGridExportHelper();
  readabilityHelper: ReadabilityHelper = new ReadabilityHelper();
  clientToastController: ClientToastController = inject(ClientToastController);

  @Output() ready: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() setExportEnable: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('grid') agGrid!: AgGridAngular;

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.agGrid?.api?.sizeColumnsToFit();
  }

  constructor(
    private allocationService: ProgressAllocationService) {
    this.gridContext = { componentParent: this };
  }

  ngOnInit(): void {
    //TODO
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  private getPayerGroupAwareNumberValue(params: any): string {
    if (params?.node?.group) {
      return '';
    }

    return AgGridFunctions.getNumberValue(params) || '';
  }

  private getPayerGroupAwarePercentValue(params: any): string {
    if (params?.node?.group) {
      return '';
    }

    return AgGridFunctions.getPercentValue(params, 1);
  }

  private getGroupLabel(params: any): string {
    if (!params?.node?.group) {
      return '';
    }

    return this.getGroupLabelCore(params);
  }

  private getGroupLabelCore(params: any): string {
    const nodeKey = params?.node?.key;
    if (nodeKey) {
      return `-> ${nodeKey}`;
    }

    const firstLeafAbbreviation = params.node?.allLeafChildren?.[0]?.data?.Payer?.Abbreviation;
    return firstLeafAbbreviation ? `-> ${firstLeafAbbreviation}` : '->';
  }

  getUserColDef(): (ColDef | ColGroupDef)[] {
    const result: any = [
      {
        headerName: `Service Year ${this.currentRequest.ServiceYear}`,
        children: [
          {
            headerName: 'User',
            colId: 'UserGroupKey',
            rowGroup: true,
            hide: true,
            valueGetter: params => params.data?.User.FirstName + ' ' + params.data?.User?.LastName || '',
          },
          { headerName: 'User', colId: 'UserDisplay',
            tooltip: AgGridFunctions.assingedUserTooltip,
            valueGetter: params => this.getGroupLabel(params),
            cellClass: 'center-cell',
            minWidth: 120,
            maxWidth: 160
          },
          { field: 'SnapshotDate', headerName: 'Snapshot Date', valueFormatter: AgGridFunctions.dateTimeToDateValue, cellClass: 'center-cell', maxWidth: 2000},
          { field: 'Progress', headerName: 'Progress', type: 'numericColumn', valueFormatter: (params) => AgGridFunctions.getPercentValue(params, 1), cellClass: 'percent', maxWidth: 150},
          { field: 'Closed', headerName: 'Closed', type: 'numericColumn', valueFormatter: AgGridFunctions.getNumberValue, cellClass: 'numberType', maxWidth: 150},
          { field: 'Open', headerName: 'Open', type: 'numericColumn', valueFormatter: AgGridFunctions.getNumberValue, cellClass: 'numberType', maxWidth: 150},
          { field: 'Pending', headerName: 'Pending', type: 'numericColumn', valueFormatter: AgGridFunctions.getNumberValue, cellClass: 'numberType', maxWidth: 150},
          { field: 'Assigned', headerName: 'Assigned', type: 'numericColumn', valueFormatter: AgGridFunctions.getNumberValue, cellClass: 'numberType', maxWidth: 150},
        ]
      }
    ];

    return result;
  }

  getPreferredActionColDef(): (ColDef | ColGroupDef)[] {

    const result: any = [
      {
        headerName: `Service Year ${this.currentRequest.ServiceYear}`,
        children: [
          {
            headerName: 'Preferred Action',
            colId: 'PreferredActionGroupKey',
            rowGroup: true,
            hide: true,
            valueGetter: params => params.data?.Name || '',
          },
          {
            headerName: 'Preferred Action',
            colId: 'PreferredActionDisplay',
            valueGetter: params => this.getGroupLabel(params),
            cellClass: 'center-cell',
            minWidth: 120,
            maxWidth: 160
          },
          { field: 'SnapshotDate', headerName: 'Snapshot Date', valueFormatter: AgGridFunctions.dateTimeToDateValue, cellClass: 'center-cell', maxWidth: 2000},
          { field: 'Progress', headerName: 'Progress', type: 'numericColumn', valueFormatter: (params) => AgGridFunctions.getPercentValue(params, 1), cellClass: 'percent', maxWidth: 150},
          { field: 'Closed', headerName: 'Closed', type: 'numericColumn', valueFormatter: AgGridFunctions.getNumberValue, cellClass: 'numberType', maxWidth: 150},
          { field: 'Open', headerName: 'Open', type: 'numericColumn', valueFormatter: AgGridFunctions.getNumberValue, cellClass: 'numberType', maxWidth: 150},
          { field: 'Pending', headerName: 'Pending', type: 'numericColumn', valueFormatter: AgGridFunctions.getNumberValue, cellClass: 'numberType', maxWidth: 150},
          { field: 'Total', headerName: 'Total', type: 'numericColumn', valueFormatter: AgGridFunctions.getNumberValue, cellClass: 'numberType', maxWidth: 150}
        ]
      }
    ];

    return result;
  }

  getPayerColDef(): (ColDef | ColGroupDef | IExportableColDef)[] {

    const result = [
      {
        headerName: `Service Year ${this.currentRequest.ServiceYear}`,
        children: [
          {
            headerName: 'Payer',
            colId: 'PayerGroupKey',
            rowGroup: true,
            hide: true,
            valueGetter: params => params.data?.Payer?.Name || params.data?.Payer?.Abbreviation || '',
          },
          {
            headerName: 'Payer',
            colId: 'Payer',
            valueGetter: params => this.getGroupLabel(params),
            cellClass: 'center-cell',
            minWidth: 120,
            maxWidth: 160
          },
          { field: 'SnapshotDate', headerName: 'Snapshot Date', valueFormatter: AgGridFunctions.dateTimeToDateValue, cellClass: 'center-cell', maxWidth: 2000},
          { field: 'Progress', 
            headerName: 'Progress', 
            type: 'numericColumn', 
            context: { exportNumberFormat: 'percent', exportPercentScale: 'fraction' },
            valueFormatter: (params) => this.getPayerGroupAwarePercentValue(params), 
            cellClass: 'percent', 
            cellStyle: { 'text-align': 'right' },
            maxWidth: 150,
          } as IExportableColDef<any, number>,
          { field: 'Closed', headerName: 'Closed', type: 'numericColumn', valueFormatter: (params) => this.getPayerGroupAwareNumberValue(params), cellClass: 'numberType', maxWidth: 150},
          { field: 'Open', headerName: 'Open', type: 'numericColumn', valueFormatter: (params) => this.getPayerGroupAwareNumberValue(params), cellClass: 'numberType', maxWidth: 150},
          { field: 'Pending', headerName: 'Pending', type: 'numericColumn', valueFormatter: (params) => this.getPayerGroupAwareNumberValue(params), cellClass: 'numberType', maxWidth: 150},
          { field: 'Total', headerName: 'Total', type: 'numericColumn', valueFormatter: (params) => this.getPayerGroupAwareNumberValue(params), cellClass: 'numberType', maxWidth: 150},
        ]
      }
    ];

    return result;
  }

  connectUser() {
    this.subs.push(this.allocationService.userAllocation.subscribe(result => {
      const items = result || [];
      const currentListSize = items.length;
      const height = currentListSize == 0 ? 300 : 107 + (currentListSize * 28);
      const width = 752;

      this.agGrid?.api?.setGridOption('columnDefs', this.getUserColDef());

      this.updateGrid(items, width, height);

      this.setExportEnable.emit(true);
    }));
  }

  connectPreferredActions() {
    this.subs.push(this.allocationService.preferredActions.subscribe(result => {
      const items = result || [];
      const currentListSize = items.length;
      const height = currentListSize == 0 ? 300 : 107 + ((currentListSize + 3) * 28);
      const width = 752;

      this.agGrid?.api?.setGridOption('columnDefs', this.getPreferredActionColDef());

      this.updateGrid(items, width, height);

      this.setExportEnable.emit(true);
    }));
  }

  connectPayer() {
    this.subs.push(this.allocationService.payerAllocation.subscribe(result => {
      const items = result || [];
      const currentListSize = items.length;
      const height = currentListSize == 0 ? 300 : 107 + (currentListSize * 28);
      const width = 752;

      this.agGrid?.api?.setGridOption('columnDefs', this.getPayerColDef());

      this.updateGrid(items, width, height);

      this.setExportEnable.emit(true);
    }));
  }

  onGridReady(event) {
    this.ready.emit(true);

    switch(this.options.mode) {
      case 'user':
        this.connectUser();
        this.exportTitle = 'User Trend'
        break;

      case 'preferred-action':
        this.connectPreferredActions();
        this.exportTitle = 'Preferred Action Trend';
        break;

      case 'payer':
        this.connectPayer();
        this.exportTitle = 'Payer Trend';
        break;

      default:
        break;
    }
  }

  onFirstDataRendered(params: FirstDataRenderedEvent) {
    const createRangeChartParams: CreateRangeChartParams = {
      cellRange: {
        columns: ['Name', 'SnapshotDate', 'Open', 'Closed', 'Pending', 'Total'],
      },
      suppressChartRanges: true,
      chartType: 'line',
      chartContainer: document.querySelector('#chart1') as any,
    };
    params.api?.createRangeChart(createRangeChartParams);
  }

  onGenerateGridData(request: ProgressAllocationSnapshotRequest) {
    
    this.currentRequest = request;

    switch(this.options.mode) {
      case 'user':
        this.allocationService.getUsersSnapshotItems(request);
        break;

      case 'preferred-action':
        this.allocationService.getPreferredActionSnapshotItems(request);
        break;

      case 'payer':
        this.allocationService.getPayerSnapshotItems(request);
        break;

      default:
        break;
    }

  }

  updateGrid(data: any[], width: number, height: number) {
    this.data = data;
    this.currentRowHeight = height;
    this.currentGridWidth = width;
    this.agGrid?.api?.setGridOption('rowData', data);

    setTimeout(() => {
      if (!this.agGrid?.api?.isDestroyed()) {
        this.agGrid?.api?.sizeColumnsToFit();
      }
    }, 200);
  }

  onExport() {
    let title = '';
    const rowGroupResolver = (params) => this.getGroupLabelCore(params);
    switch(this.options.mode) {
      case 'user':
        title = 'UserProgressSnapshotTrendExport';
        break;
      case 'preferred-action':
        title = 'PreferredActionSnapshotTrendExport';
        break;
      case 'payer':
        title = 'PayerProgressSnapshotTrendExport';
        break;
      default:
        title = '';
        break;
    }
    const filename =`${title}_${this.currentRequest.ServiceYear}`;

    this.agGridExportHelper.onExport(
      filename,
      this.exportTitle,
      this.getFilters(),
      this.agGrid,
      [],
      undefined,
      undefined,
      rowGroupResolver
    );
  }
  
  private getFilters(): IQueryFilterRow[] {
    const interval = this.getReadableInterval(this.currentRequest.Interval);
    const filters: IQueryFilterRow[] = [
      { label: 'Mode', value: this.options.mode, type: 'String' },
      { label: 'Service Year', value: this.currentRequest.ServiceYear.toString(), type: 'Number' },
      { label: 'Date Range', value: `${this.currentRequest.StartDate} - ${this.currentRequest.EndDate}`, type: 'String' },
      { label: 'Interval', value: interval, type: 'String' },
    ];

    if (this.options.mode === 'payer') {
      filters.push({
        label: 'Payer',
        value: this.getSelectedPayerAbbreviations(),
        type: 'String'
      });
    }

    if (this.options.mode === 'user') {
      filters.push({
        label: 'User',
        value: this.getSelectedUsers(),
        type: 'String'
      });
    }

    return filters;
  }

  private getSelectedPayerAbbreviations(): string {
    try {
      return this.readabilityHelper.getSelectedPayerAbbreviationsByIds(this.currentRequest?.IDs, this.payerOptions);
    } catch {
      this.clientToastController.displayToast('Payer IDs could not be reconciled.', 3000);
      return '';
    }
  }

  private getSelectedUsers(): string {
    try {
      return this.userOptions
        .filter((option: UserOption) => {
          return option.selected;
        })
        .map(
          (option: UserOption) =>
            option.user?.given_name + " " + (option.user?.family_name || "")
        )
        .join(", ");
    } catch {
      this.clientToastController.displayToast('User IDs could not be reconciled.', 3000);
      return '';
    }
  }

  private getReadableInterval(interval: number | string): string {
    const intervalNumber = Number(interval);

    if (this.intervalLabelMap[intervalNumber]) {
      return this.intervalLabelMap[intervalNumber];
    }

    if (!Number.isNaN(intervalNumber) && intervalNumber > 0) {
      return `${intervalNumber} Days`;
    }

    return 'Unknown';
  }

}
